import React from "react";
import * as Mui from "@mui/material";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { designColors, StatCard } from "services/constants/designTokens";

// Same accent/tint mapping as the other split screens' status cards (see
// AdvanceSalaryStatusCards.js) so "Pending"/"Approved"/"Paid"/"Rejected"
// read the same color everywhere in Team Zone — this screen also mirrors
// the "All / Pending / Approved / Paid / Rejected / Cancelled" tiles My
// Zone > My Requests > Expense Claims already shows for the same data.
const STATUS_CARD_DEFS = [
  {
    key: "Total Requests",
    label: "All",
    detail: "All Requests",
    icon: ListAltIcon,
    accent: "#7C3AED",
    tint: "#F3E8FF",
  },
  {
    key: "Pending",
    label: "Pending",
    detail: "Requests",
    icon: AccessTimeIcon,
    accent: "#F97316",
    tint: "#FFF7ED",
  },
  {
    key: "Approved",
    label: "Approved",
    detail: "Requests",
    icon: CheckCircleOutlineIcon,
    accent: "#16A34A",
    tint: "#EAFBF1",
  },
  {
    key: "Paid",
    label: "Paid in salary",
    detail: "Requests",
    icon: AccountBalanceOutlinedIcon,
    accent: "#2563EB",
    tint: "#EFF6FF",
  },
  {
    key: "Rejected",
    label: "Rejected",
    detail: "Requests",
    icon: HighlightOffIcon,
    accent: "#EF4444",
    tint: "#FEF2F2",
  },
  {
    key: "Cancelled",
    label: "Cancelled",
    detail: "Requests",
    icon: RemoveCircleOutlineIcon,
    accent: designColors.slate600,
    tint: designColors.slate100,
  },
];

// Bucket the API's `status` string into one of the canonical statuses above
// (case/spelling tolerant).
export const canonicalExpenseStatus = (status) => {
  const key = (status || "").trim().toLowerCase();
  if (key === "pending") return "Pending";
  if (key === "approved") return "Approved";
  if (key === "paid") return "Paid";
  if (key === "rejected") return "Rejected";
  if (key === "cancelled" || key === "canceled") return "Cancelled";
  return status;
};

// Single source of truth for a status's accent/tint pair, shared by these
// cards and DetailPanel's header chip.
export const expenseStatusVisual = (status) => {
  const canonical = canonicalExpenseStatus(status);
  return (
    STATUS_CARD_DEFS.find((card) => card.key === canonical) || {
      key: canonical,
      accent: designColors.slate600,
      tint: designColors.slate100,
    }
  );
};

export const ExpenseStatusCards = ({ counts, activeFilter, onCardClick }) => {
  return (
    <Mui.Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
        "@media (max-width:599.95px)": {
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          "& > :last-child:nth-child(odd)": { gridColumn: "1 / -1" },
        },
        gap: 1.5,
        mb: 2,
      }}
    >
      {STATUS_CARD_DEFS.map((card) => {
        const isTotalCard = card.key === "Total Requests";
        return (
          <StatCard
            activeBorder
            key={card.key}
            icon={card.icon}
            title={card.label}
            value={counts?.[card.key] || 0}
            detail={card.detail}
            accent={card.accent}
            tint={card.tint}
            isActive={isTotalCard ? !activeFilter : activeFilter === card.key}
            onClick={onCardClick ? () => onCardClick(card.key) : undefined}
          />
        );
      })}
    </Mui.Box>
  );
};
